import React from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Box } from '@mui/material';
import theme from './theme/theme';
import Navbar from './components/Navbar/Navbar';
import Home from './pages/Home/Home';
import AboutPage from './pages/AboutPage/AboutPage';
import VolunteerPage from './pages/VolunteerPage/VolunteerPage';
import SignUpPage from './pages/SignUpPage/SignUpPage';
import SignInPage from './pages/SignInPage/SignInPage';
import ContactPage from './pages/ContactPage/ContactPage';
import QRGenerator from './pages/QRGenerator/QRGenerator';
import QRScanner from './pages/QRScanner/QRScanner';
import PublicBinReport from './pages/PublicBinReport/PublicBinReport';
import NearbyBinsPage from './pages/NearbyBinsPage/NearbyBinsPage';
import NearbyBinsMap from './components/Map/NearbyBinsMap';
import ReportBin from './pages/ReportBin/ReportBin';
import ReportSuccess from './pages/ReportSuccess/ReportSuccess';
import Reports from './pages/Reports/Reports';
import ReportManagement from './components/ReportManagement/ReportManagement';
import Dashboard from './pages/Dashboard/Dashboard';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import Layout from './components/layout/Layout';
import FullScreenLayout from './components/layout/FullScreenLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { ROLES } from './constants/roles';
import WorkerManagement from './pages/Management/WorkerManagement';
import BinManagement from './pages/Management/BinManagement';
import BinAnalytics from './pages/Management/BinAnalytics';
import MapView from './pages/MapView/MapView';
import { BinList, BinActivity, BinDetail } from './components/BinManagement';
import AddBinSimplified from './components/BinManagement/AddBin';

import { WorkerList, WorkerForm, WorkerActivity, WorkerDetail, WorkerBins } from './components/WorkerManagement';
import WorkerProfile from './pages/Management/WorkerProfile';
import WorkerOwnProfile from './pages/Worker/WorkerOwnProfile';
import MyTasksPage from './pages/Worker/MyTasksPage';
import WorkerHeader from './components/WorkerManagement/WorkerHeader';
import MapsProvider from './components/Maps/MapsProvider';
import ZoneManagement from './pages/Management/ZoneManagement';
import { ZoneList } from './components/ZoneManagement';
import UserManagement from './pages/Management/UserManagement';
import UserProfile from './pages/Management/UserProfile';
import VolunteerManagement from './pages/Management/VolunteerManagement';
import VolunteerProfile from './pages/Management/VolunteerProfile';
import VehicleManagementPage from './pages/VehicleManagement/VehicleManagementPage';
import TaskManagementPage from './pages/TaskManagement/TaskManagementPage';


const WorkerLayout = ({ children }) => {
  return (
    <Layout>
      <WorkerHeader />
      {children}
    </Layout>
  );
};

const PublicLayout = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Outlet />
      </main>
    </>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Route>
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signin" element={<SignInPage />} />
          
          
          {/* Nearby Bins Route - User only */}
          <Route path="/nearby-bins" element={
            <ProtectedRoute allowedRoles={[ROLES.USER]}>
              <NearbyBinsPage />
            </ProtectedRoute>
          } />
          
          {/* Bin Report Route - require auth to report; allow unauthenticated to view info */}
          <Route path="/report-bin/:binId" element={
            <ProtectedRoute>
              <ReportBin />
            </ProtectedRoute>
          } />
          {/* Make bin report accessible only to authenticated users */}
          <Route path="/bin-report/:binId?" element={
            <ProtectedRoute>
              <PublicBinReport />
            </ProtectedRoute>
          } />
          
          {/* Report Success Route */}
          <Route path="/reports/success" element={
            <ProtectedRoute>
              <ReportSuccess />
            </ProtectedRoute>
          } />
          
          {/* User Reports Route */}
          <Route path="/reports" element={
            <ProtectedRoute>
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          } />

          {/* User Rewards Route */}
          <Route path="/rewards" element={
            <ProtectedRoute>
              <Layout>
                <div className="p-6">
                  <h1 className="text-2xl font-bold mb-4">My Rewards</h1>
                  <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-gray-600">Rewards system coming soon! You earn 10 points for each report submitted.</p>
                  </div>
                </div>
              </Layout>
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path="/dashboard/*" element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route index element={<Navigate to={`/dashboard/${user?.role || 'user'}`} replace />} />
                  <Route path=":role" element={<Dashboard />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />

          {/* Dashboard routes with specific layouts */}
          <Route path="/dashboard/admin" element={<ProtectedRoute allowedRoles={[ROLES.ADMIN]}><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/dashboard/worker" element={<ProtectedRoute allowedRoles={[ROLES.WORKER]}><WorkerLayout><Dashboard /></WorkerLayout></ProtectedRoute>} />
          <Route path="/dashboard/volunteer" element={<ProtectedRoute allowedRoles={[ROLES.VOLUNTEER]}><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/dashboard/user" element={<ProtectedRoute allowedRoles={[ROLES.USER]}><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Navigate to={`/dashboard/${user?.role || 'user'}`} replace />
            </ProtectedRoute>
          } />

          {/* Worker Routes */}
          <Route path="/worker/*" element={
            <ProtectedRoute allowedRoles={[ROLES.WORKER]}>
              <WorkerLayout>
                <Routes>
                  <Route path="profile" element={<WorkerOwnProfile />} />
                  <Route path="bins" element={<BinList />} />
                </Routes>
              </WorkerLayout>
            </ProtectedRoute>
          } />

          {/* Management Routes */}
          <Route path="/management/*" element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <Layout>
                <Routes>
                    <Route index element={<BinManagement />} />
                    <Route path="zones/*" element={
                      <Routes>
                        <Route index element={<ZoneManagement />} />
                      </Routes>
                    } />
                    <Route path="bins/*" element={
                      <Routes>
                        <Route index element={<BinList />} />
                        <Route path="new" element={<AddBinSimplified />} />
                        <Route path=":id" element={<BinDetail />} />
                        <Route path=":id/history" element={<BinActivity />} />
                      </Routes>
                    } />
                    <Route path="tasks" element={<TaskManagementPage />} />
                    <Route path="workers/*" element={
                      <Routes>
                        <Route index element={<WorkerManagement />} />
                        <Route path="list" element={<WorkerList />} />
                        <Route path="new" element={<WorkerForm />} />
                        <Route path="edit/:id" element={<WorkerForm />} />
                        <Route path=":id" element={<WorkerProfile />} />
                        <Route path=":id/activity" element={<WorkerActivity />} />
                        <Route path=":id/bins" element={<WorkerBins />} />
                      </Routes>
                    } />
                    <Route path="users/*" element={
                      <Routes>
                        <Route index element={<UserManagement />} />
                        <Route path=":id" element={<UserProfile />} />
                      </Routes>
                    } />
                    <Route path="volunteers/*" element={
                      <Routes>
                        <Route index element={<VolunteerManagement />} />
                        <Route path=":id" element={<VolunteerProfile />} />
                      </Routes>
                    } />
                    <Route path="vehicles/*" element={
                      <Routes>
                        <Route index element={<VehicleManagementPage />} />
                      </Routes>
                    } />
                    <Route path="analytics" element={<BinAnalytics />} />
                    <Route path="reports" element={<ReportManagement />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />

          {/* Other Protected Routes */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Worker Specific Routes */}
          <Route path="/worker/profile" element={
            <ProtectedRoute allowedRoles={[ROLES.WORKER]}>
              <WorkerLayout>
                <WorkerOwnProfile />
              </WorkerLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/worker/my-tasks" element={
            <ProtectedRoute allowedRoles={[ROLES.WORKER]}>
              <WorkerLayout>
                <MyTasksPage />
              </WorkerLayout>
            </ProtectedRoute>
          } />

          <Route path="/qr-generator" element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <Layout>
                <QRGenerator />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/qr-scanner" element={
            <ProtectedRoute allowedRoles={[ROLES.USER, ROLES.WORKER, ROLES.VOLUNTEER]}>
              <QRScanner />
            </ProtectedRoute>
          } />

          <Route path="/scanner" element={
            <ProtectedRoute>
              <QRScanner />
            </ProtectedRoute>
          } />

          <Route path="/map-view" element={
            <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
              <FullScreenLayout>
                <MapView />
              </FullScreenLayout>
            </ProtectedRoute>
          } />


          {/* Redirect old routes to new structure */}
          <Route path="/bins/*" element={<Navigate to="/management/bins" replace />} />

          <Route path="/workers/*" element={<Navigate to="/management/workers" replace />} />
          <Route path="/bin-analytics" element={<Navigate to="/management/analytics" replace />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
};

const AppContent = () => {
  return (
    <div className="min-h-screen bg-white">
      <AppRoutes />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <MapsProvider>
          <AppContent />
        </MapsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;